/**
 * Placeフィルタのロジックと型定義
 */

import { subMonths } from 'date-fns'

import type { Place } from '@/api/types/overland'

// =============================================================================
// 型定義
// =============================================================================

/** フィルタの種類 */
export type FilterType =
  | 'is_custom'
  | 'provider'
  | 'category'
  | 'genre'
  | 'visit_count'
  | 'last_visited'
  | 'has_image'
  | 'has_address'
  | 'has_google_id'
  | 'has_wifi'
  | 'has_custom_name'

/** 比較演算子 */
export type NumberOperator = 'eq' | 'gte' | 'lte'

/** 期間プリセット */
export type DatePreset = 'this_month' | 'last_3_months' | 'last_6_months' | 'over_1_year' | 'never'

/** 個別フィルタの値 */
export type FilterValue =
  | { type: 'is_custom'; value: boolean }
  | { type: 'provider'; values: string[] }
  | { type: 'category'; values: string[] }
  | { type: 'genre'; values: string[] }
  | { type: 'visit_count'; operator: NumberOperator; value: number }
  | { type: 'last_visited'; preset: DatePreset }
  | { type: 'has_image'; value: boolean }
  | { type: 'has_address'; value: boolean }
  | { type: 'has_google_id'; value: boolean }
  | { type: 'has_wifi'; value: boolean }
  | { type: 'has_custom_name'; value: boolean }

/** フィルタ項目の定義 */
export type FilterDefinition = {
  type: FilterType
  label: string
}

// =============================================================================
// 定数
// =============================================================================

export const FILTER_DEFINITIONS: FilterDefinition[] = [
  { type: 'is_custom', label: '種別' },
  { type: 'provider', label: '提供元' },
  { type: 'category', label: 'カテゴリ' },
  { type: 'genre', label: 'ジャンル' },
  { type: 'visit_count', label: '訪問回数' },
  { type: 'last_visited', label: '最終訪問日' },
  { type: 'has_image', label: 'メディア' },
  { type: 'has_address', label: '住所' },
  { type: 'has_google_id', label: 'Google Place ID' },
  { type: 'has_wifi', label: 'WiFiパターン' },
  { type: 'has_custom_name', label: 'カスタム名' },
]

export const NUMBER_OPERATORS: { value: NumberOperator; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'gte', label: '≥' },
  { value: 'lte', label: '≤' },
]

export const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'this_month', label: '今月' },
  { value: 'last_3_months', label: '過去3ヶ月' },
  { value: 'last_6_months', label: '過去6ヶ月' },
  { value: 'over_1_year', label: '1年以上前' },
  { value: 'never', label: '未訪問' },
]

// =============================================================================
// フィルタロジック
// =============================================================================

/** フィルタを適用 */
export function applyFilters(places: Place[], filters: FilterValue[]): Place[] {
  if (filters.length === 0) return places
  return places.filter((place) => filters.every((filter) => matchFilter(place, filter)))
}

function matchFilter(place: Place, filter: FilterValue): boolean {
  switch (filter.type) {
    case 'is_custom':
      return place.is_custom === filter.value

    case 'provider':
      return filter.values.includes(place.provider ?? 'none')

    case 'category':
      return place.categories.some(
        (c) => filter.values.includes(c.name) || filter.values.includes(c.display_name ?? ''),
      )

    case 'genre':
      return place.genres.some((g) => filter.values.includes(g.name))

    case 'visit_count':
      switch (filter.operator) {
        case 'eq':
          return place.visit_count === filter.value
        case 'gte':
          return place.visit_count >= filter.value
        case 'lte':
          return place.visit_count <= filter.value
      }
      return false

    case 'last_visited':
      return matchDatePreset(place.last_visited_at, filter.preset)

    case 'has_image':
      return filter.value ? (place.photo_count ?? 0) > 0 : (place.photo_count ?? 0) === 0

    case 'has_address':
      return filter.value ? !!place.address : !place.address

    case 'has_google_id':
      return filter.value ? !!place.google_place_id : !place.google_place_id

    case 'has_wifi':
      return filter.value ? place.wifi_patterns.length > 0 : place.wifi_patterns.length === 0

    case 'has_custom_name':
      return filter.value ? !!place.custom_name : !place.custom_name
  }
  return false
}

function matchDatePreset(dateStr: string | null, preset: DatePreset): boolean {
  if (preset === 'never') return !dateStr
  if (!dateStr) return false

  const date = new Date(dateStr)
  const now = new Date()

  switch (preset) {
    case 'this_month':
      return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()
    case 'last_3_months':
      return date >= subMonths(now, 3)
    case 'last_6_months':
      return date >= subMonths(now, 6)
    case 'over_1_year':
      return date < subMonths(now, 12)
  }
}

/** フィルタのラベルを生成 */
export function getFilterLabel(filter: FilterValue): string {
  switch (filter.type) {
    case 'is_custom':
      return filter.value ? 'マイプレイス' : '通常'
    case 'provider':
      return filter.values.map((v) => (v === 'none' ? 'なし' : v)).join(', ')
    case 'category':
      return filter.values.join(', ')
    case 'genre':
      return filter.values.join(', ')
    case 'visit_count': {
      const op = NUMBER_OPERATORS.find((o) => o.value === filter.operator)
      return `${op?.label ?? ''} ${filter.value}`
    }
    case 'last_visited':
      return DATE_PRESETS.find((p) => p.value === filter.preset)?.label ?? ''
    case 'has_image':
      return filter.value ? 'あり' : 'なし'
    case 'has_address':
      return filter.value ? 'あり' : 'なし'
    case 'has_google_id':
      return filter.value ? 'あり' : 'なし'
    case 'has_wifi':
      return filter.value ? 'あり' : 'なし'
    case 'has_custom_name':
      return filter.value ? 'あり' : 'なし'
  }
}

/** デフォルトフィルタ値を生成 */
export function createDefaultFilter(type: FilterType): FilterValue {
  switch (type) {
    case 'is_custom':
      return { type: 'is_custom', value: true }
    case 'provider':
      return { type: 'provider', values: [] }
    case 'category':
      return { type: 'category', values: [] }
    case 'genre':
      return { type: 'genre', values: [] }
    case 'visit_count':
      return { type: 'visit_count', operator: 'gte', value: 1 }
    case 'last_visited':
      return { type: 'last_visited', preset: 'this_month' }
    case 'has_image':
      return { type: 'has_image', value: true }
    case 'has_address':
      return { type: 'has_address', value: true }
    case 'has_google_id':
      return { type: 'has_google_id', value: true }
    case 'has_wifi':
      return { type: 'has_wifi', value: true }
    case 'has_custom_name':
      return { type: 'has_custom_name', value: true }
  }
}
